import { createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  applyCompletionSnapshot,
  archiveHabitMutation,
  type CompletionStatus,
  createHabitMutation,
  deleteHabitMutation,
  deleteReminderMutation,
  getCompletionSnapshot,
  getHabitByIdQuery,
  type HabitDraft,
  incrementCompletionAmountMutation,
  reorderHabitsMutation,
  restoreHabitMutation,
  setCompletionMutation,
  setCompletionNoteMutation,
  toggleCompletionMutation,
  trashHabitMutation,
  updateHabitMutation,
  upsertReminderMutation,
} from "@habits/shared";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { registerUndo, type UndoMessages } from "@/lib/undo";

type HabitUpdates = Parameters<typeof updateHabitMutation>[2];

/**
 * Habits domain mutations with submission state and uniform error
 * handling. PowerSync queries auto-invalidate upon table changes.
 *
 * UNDO-TRASH-LIGHT (L1): each user mutation captures PREVIOUS state and
 * registers its inverse on undo stack (pill "· Undo" + Ctrl/Cmd+Z).
 * Inverse re-enters through these same mutations (standard PowerSync
 * write-path) — undo is just another compensating write, never a rewind.
 */
export function useHabitMutations() {
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const run = async <T>(fn: () => Promise<T>): Promise<T | null> => {
    setIsSubmitting(true);
    try {
      return await fn();
    } catch (err: unknown) {
      console.error("[habit-mutation]", err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const requireDb = () => {
    const db = drizzle();
    if (!db) {
      throw new Error("PowerSync no inicializado");
    }
    return db;
  };

  const requireUser = () => {
    const uid = session.userId();
    if (!uid) {
      throw new Error("Session unavailable");
    }
    return uid;
  };

  /** Common strings for undo pill (already translated upon registration). */
  const undoMessages = (done: string): UndoMessages => ({
    done,
    undoLabel: t(msg`Undo`),
    undone: t(msg`Undone`),
    failed: t(msg`Couldn't undo`),
  });

  /** Standard inverse for check-in mutations: re-apply previous snapshot. */
  const undoCompletion = async (
    done: string,
    habitId: string,
    dayKey: string,
    fn: () => Promise<void>,
  ) => {
    const before = await getCompletionSnapshot(requireDb(), habitId, dayKey);
    await fn();
    registerUndo(undoMessages(done), async () =>
      applyCompletionSnapshot(requireDb(), requireUser(), habitId, dayKey, before),
    );
  };

  return {
    isSubmitting,
    createHabit: async (draft: HabitDraft) =>
      run(async () => {
        const id = await createHabitMutation(requireDb(), requireUser(), draft);
        // Inverse: permanent deletion (newly created habit does not yet have history to protect).
        registerUndo(undoMessages(t(msg`Habit created`)), async () =>
          deleteHabitMutation(requireDb(), id),
        );
        return id;
      }),
    updateHabit: async (id: string, updates: HabitUpdates) =>
      run(async () => {
        const before = (await getHabitByIdQuery(requireDb(), id))[0];
        await updateHabitMutation(requireDb(), id, updates);
        if (before) {
          // Inverse: restore ONLY fields that this edit touched (explicit
          // field-by-field mapping — type-safe, without asserts).
          const prev: HabitUpdates = {};
          if (updates.name !== undefined) {
            prev.name = before.name ?? "";
          }
          if (updates.icon !== undefined) {
            prev.icon = before.icon;
          }
          if (updates.color !== undefined) {
            prev.color = before.color;
          }
          if (updates.schedule_kind !== undefined) {
            prev.schedule_kind = before.schedule_kind ?? "daily";
          }
          if (updates.weekdays !== undefined) {
            prev.weekdays = before.weekdays;
          }
          if (updates.times_per_week !== undefined) {
            prev.times_per_week = before.times_per_week;
          }
          if (updates.sort_order !== undefined) {
            prev.sort_order = before.sort_order ?? 0;
          }
          if (updates.archived !== undefined) {
            prev.archived = before.archived ?? 0;
          }
          if (updates.goal_kind !== undefined) {
            prev.goal_kind = before.goal_kind ?? "binary";
          }
          if (updates.target_amount !== undefined) {
            prev.target_amount = before.target_amount;
          }
          if (updates.target_unit !== undefined) {
            prev.target_unit = before.target_unit;
          }
          registerUndo(undoMessages(t(msg`Habit updated`)), async () =>
            updateHabitMutation(requireDb(), id, prev),
          );
        }
      }),
    archiveHabit: async (id: string, archived: boolean) =>
      run(async () => {
        await archiveHabitMutation(requireDb(), id, archived);
        registerUndo(
          undoMessages(archived ? t(msg`Habit archived`) : t(msg`Habit unarchived`)),
          async () => archiveHabitMutation(requireDb(), id, !archived),
        );
      }),
    /** Deleting from UI = send to trash (L2). Undo = restore. */
    deleteHabit: async (id: string) =>
      run(async () => {
        await trashHabitMutation(requireDb(), id);
        registerUndo(undoMessages(t(msg`Habit moved to trash`)), async () =>
          restoreHabitMutation(requireDb(), id),
        );
      }),
    /** Restore from trash. Undo = return to trash. */
    restoreHabit: async (id: string) =>
      run(async () => {
        await restoreHabitMutation(requireDb(), id);
        registerUndo(undoMessages(t(msg`Habit restored`)), async () =>
          trashHabitMutation(requireDb(), id),
        );
      }),
    /** PERMANENT deletion from trash. Irreversible: WITHOUT undo. */
    deleteHabitForever: async (id: string) => run(async () => deleteHabitMutation(requireDb(), id)),
    reorderHabits: async (orderedIds: string[]) =>
      run(async () => reorderHabitsMutation(requireDb(), orderedIds)),
    toggleCompletion: async (habitId: string, dayKey: string, current: CompletionStatus | null) =>
      run(async () => {
        let next: CompletionStatus | null = null;
        await undoCompletion(
          current === null ? t(msg`Marked as done`) : t(msg`Mark removed`),
          habitId,
          dayKey,
          async () => {
            next = await toggleCompletionMutation(
              requireDb(),
              requireUser(),
              habitId,
              dayKey,
              current,
            );
          },
        );
        return next;
      }),
    setCompletion: async (
      habitId: string,
      dayKey: string,
      status: CompletionStatus | null,
      note?: string | null,
      amount?: number | null,
    ) =>
      run(async () => {
        const label =
          status === "skipped"
            ? t(msg`Day frozen`)
            : status === null
              ? t(msg`Mark removed`)
              : t(msg`Marked as done`);
        let result: CompletionStatus | null = null;
        await undoCompletion(label, habitId, dayKey, async () => {
          result = await setCompletionMutation(
            requireDb(),
            requireUser(),
            habitId,
            dayKey,
            status,
            note,
            amount,
          );
        });
        return result;
      }),
    incrementAmount: async (habitId: string, dayKey: string, delta: number, max: number) =>
      run(async () => {
        let result: number | null = null;
        await undoCompletion(t(msg`Progress updated`), habitId, dayKey, async () => {
          result = await incrementCompletionAmountMutation(
            requireDb(),
            requireUser(),
            habitId,
            dayKey,
            delta,
            max,
          );
        });
        return result;
      }),
    setCompletionNote: async (habitId: string, dayKey: string, note: string | null) =>
      run(async () =>
        undoCompletion(t(msg`Note saved`), habitId, dayKey, async () =>
          setCompletionNoteMutation(requireDb(), requireUser(), habitId, dayKey, note),
        ),
      ),
    upsertReminder: async (
      habitId: string,
      timeOfDay: string,
      opts?: { id?: string; enabled?: boolean },
    ) =>
      run(async () => upsertReminderMutation(requireDb(), requireUser(), habitId, timeOfDay, opts)),
    deleteReminder: async (id: string) => run(async () => deleteReminderMutation(requireDb(), id)),
  };
}
